
import { useEffect, useState } from "react"
import { MessageCircle, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/lib/auth-context"

declare global {
  interface Window {
    Intercom: (...args: unknown[]) => void
    intercomSettings: Record<string, unknown>
  }
}

export function IntercomProvider() {
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const { user } = useAuth()

  useEffect(() => {
    const appId = import.meta.env.VITE_INTERCOM_APP_ID
    if (!appId) {
      console.warn("[Intercom] VITE_INTERCOM_APP_ID not configured")
      return
    }

    const intercomConfig: Record<string, unknown> = {
      api_base: "https://api-iam.intercom.io",
      app_id: appId,
      action_color: "#ed4c4c",
      background_color: "#ed4c4c",
      alignment: "right",
      horizontal_padding: 20,
      vertical_padding: 20,
      hide_default_launcher: true,
      company: {
        company_id: "heysalad",
        name: "HeySalad",
        plan: "early-access",
      },
      custom_attributes: {
        app_name: "Irene",
        app_description: "AI-powered investor relations platform",
        features:
          "Deck Analyzer, AI Investor Search, Investor Updates, Deal Room, Pipeline CRM",
        powered_by: "GPT-4o, Tavily, Browserless",
      },
    }

    if (user) {
      intercomConfig.user_id = user.id
      intercomConfig.name = user.name
      intercomConfig.email = user.email
    }

    window.intercomSettings = intercomConfig

    function bootIntercom() {
      if (!window.Intercom) return
      window.Intercom("boot", intercomConfig)
      window.Intercom("onUnreadCountChange", (count: number) => {
        setUnreadCount(count)
      })
      window.Intercom("onShow", () => setIsOpen(true))
      window.Intercom("onHide", () => setIsOpen(false))
    }

    if (!document.getElementById("intercom-script")) {
      const script = document.createElement("script")
      script.id = "intercom-script"
      script.async = true
      script.src = `https://widget.intercom.io/widget/${appId}`
      document.head.appendChild(script)
      script.onload = bootIntercom
    } else if (window.Intercom) {
      window.Intercom("update", intercomConfig)
    }

    return () => {
      if (window.Intercom) {
        window.Intercom("shutdown")
      }
    }
  }, [user])

  function handleToggle() {
    if (!window.Intercom) return
    if (isOpen) {
      window.Intercom("hide")
    } else {
      window.Intercom("show")
    }
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={cn(
        "fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg transition-all duration-200 hover:scale-105 active:scale-95",
        "bg-primary text-primary-foreground",
        isOpen && "rotate-0"
      )}
      aria-label={isOpen ? "Close chat" : "Open chat"}
    >
      {isOpen ? (
        <X className="h-6 w-6" />
      ) : (
        <>
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-[10px] font-bold text-card">
              {unreadCount}
            </span>
          )}
        </>
      )}
    </button>
  )
}
